import LZString from 'lz-string';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isRecord } from '@revelio/core/is-record';
import type { ScreenerReqSorting } from '@revelio/data-access';

const isValidSorting = (sorting: unknown): sorting is ScreenerReqSorting => {
  if (!isRecord(sorting)) {
    return false;
  }

  return (
    typeof sorting.sort_by === 'string' &&
    typeof sorting.ascending === 'boolean' &&
    typeof sorting.num_col === 'number'
  );
};

const getSortingFromCompressed = (
  compressed: string,
  source: 'url' | 'storage'
): ScreenerReqSorting | undefined => {
  const decompressed =
    source === 'url'
      ? LZString.decompressFromEncodedURIComponent(compressed)
      : LZString.decompressFromUTF16(compressed);

  if (!decompressed) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(decompressed);
    if (!isValidSorting(parsed)) {
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
};

export const useScreenerSortingSync = (): {
  sorting: ScreenerReqSorting | undefined;
  setSorting: (sorting: ScreenerReqSorting | undefined) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortingParam = searchParams.get('sorting');
  const sorting = sortingParam
    ? getSortingFromCompressed(sortingParam, 'url')
    : undefined;

  useEffect(() => {
    if (!sortingParam) {
      return;
    }

    if (!sorting) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('sorting');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, sorting, sortingParam]);

  const setSorting = (nextSorting: ScreenerReqSorting | undefined) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!nextSorting) {
      nextParams.delete('sorting');
    } else {
      const compressed = LZString.compressToEncodedURIComponent(
        JSON.stringify(nextSorting)
      );
      nextParams.set('sorting', compressed);
    }
    setSearchParams(nextParams, { replace: true });
  };

  return { sorting, setSorting };
};
